/**
 * useSwapQuote — fetches swap quotes from the V4 Quoter contract.
 *
 * Uses the on-chain Quoter's quoteExactInputSingle to simulate the actual
 * swap path (including tick-boundary crossing), replacing the client-side
 * estimateSwapOutput approximation that returned 0 when getLiquidity()=0
 * at a tick boundary.
 *
 * Depends on usePoolState for fee pips, sqrtPriceX96, and noLiquidity detection.
 *
 * ARCH-27: Contract interaction hook pattern
 * Story 5.1 Task 5
 */

import { useQuery } from "@tanstack/react-query";
import { useDeferredValue } from "react";
import { parseUnits } from "viem";
import { simulateContract } from "wagmi/actions";

import { quoterAbi } from "../../lib/abis/quoter";
import {
	CLANKER_TICK_SPACING,
	DYNAMIC_FEE_FLAG,
	getChainAddresses,
} from "../../lib/chain-addresses";
import { toCheckedHex } from "../../lib/hex-utils";
import { calculateMinimumOutput } from "../../lib/swap-math";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";
import { usePoolState } from "./use-pool-state";

/** Precision scale for bigint → float conversion (10^18). */
const RATE_SCALE = 10n ** 18n;

/** Fee denominator in pips (1,000,000 = 100%). */
const PIPS_DENOMINATOR = 1_000_000n;

export type SwapQuote = {
	amountOut: bigint;
	priceImpact: number;
	feeBreakdown: {
		/** V4 LP fee in pips (hundredths of bps). 3000 = 0.30% */
		lpFeePips: number;
		/** V4 protocol fee in pips. 0 = no protocol fee */
		protocolFeePips: number;
		lpFeeAmount: bigint;
		protocolFeeAmount: bigint;
	};
	effectiveRate: number;
	minimumReceived: bigint;
	direction: "buy" | "sell";
};

type UseSwapQuoteParams = {
	tokenAddress: string;
	poolAddress: string;
	amountIn: string;
	direction: "buy" | "sell";
	slippageBps: number;
	tokenDecimals?: number;
};

type UseSwapQuoteReturn = {
	quote: SwapQuote | null;
	isLoading: boolean;
	error: Error | null;
	noLiquidity: boolean;
	refetch: () => void;
};

/**
 * Determine if zeroForOne based on token ordering.
 * token0 = min(tokenAddress, WETH) by lowercase hex.
 * For "buy" (buying token with ETH): zeroForOne depends on which is token0.
 * For "sell" (selling token for ETH): opposite direction.
 */
function getZeroForOne(tokenAddress: string, direction: "buy" | "sell", chainId: number): boolean {
	const addresses = getChainAddresses(chainId);
	if (!addresses) {
		return false;
	}
	const tokenLower = tokenAddress.toLowerCase();
	const wethLower = addresses.weth.toLowerCase();
	const tokenIsToken0 = tokenLower < wethLower;

	if (direction === "buy") {
		// Buying token with ETH: ETH is input
		// If WETH is token0: zeroForOne = true (selling token0 for token1)
		// If WETH is token1: zeroForOne = false
		return !tokenIsToken0;
	}
	// Selling token for ETH: token is input
	// If token is token0: zeroForOne = true
	// If token is token1: zeroForOne = false
	return tokenIsToken0;
}

const Q192 = 2n ** 192n;
const PRECISION = 10n ** 18n;

/**
 * Compute price impact by comparing the quoter execution price to the mid-price from sqrtPriceX96.
 */
function computePriceImpact(
	sqrtPriceX96: bigint,
	amountIn: bigint,
	amountOut: bigint,
	zeroForOne: boolean
): number {
	if (sqrtPriceX96 === 0n || amountIn === 0n || amountOut === 0n) return 0;

	const midPriceScaled = (sqrtPriceX96 * sqrtPriceX96 * PRECISION) / Q192;
	const execPriceScaled = zeroForOne
		? (amountOut * PRECISION) / amountIn
		: (amountIn * PRECISION) / amountOut;

	if (midPriceScaled === 0n) return 0;
	return Math.abs(Number(midPriceScaled - execPriceScaled) / Number(midPriceScaled)) * 100;
}

/**
 * Build the sorted pool key for V4 Quoter calls.
 * currency0 < currency1 by address, hooks = pool contract address.
 */
function buildPoolKey(tokenAddress: string, poolAddress: string, weth: string) {
	const tokenHex = toCheckedHex(tokenAddress);
	const wethHex = toCheckedHex(weth);
	const currency0 = tokenHex < wethHex ? tokenHex : wethHex;
	const currency1 = tokenHex < wethHex ? wethHex : tokenHex;

	return {
		currency0,
		currency1,
		fee: DYNAMIC_FEE_FLAG,
		tickSpacing: CLANKER_TICK_SPACING,
		hooks: toCheckedHex(poolAddress),
	} as const;
}

export function useSwapQuote(params: UseSwapQuoteParams): UseSwapQuoteReturn {
	const {
		tokenAddress,
		poolAddress,
		amountIn,
		direction,
		slippageBps,
		tokenDecimals = 18,
	} = params;

	const poolState = usePoolState(tokenAddress, poolAddress);
	const deferredAmount = useDeferredValue(amountIn);

	// Detect zero-liquidity pool (initialized but no ETH seeded)
	const noLiquidity = Boolean(
		poolState.sqrtPriceX96 && poolState.sqrtPriceX96 > 0n && poolState.liquidity === 0n
	);

	// Parse the deferred input amount once — shared by enabled guard and queryFn
	const inputDecimals = direction === "buy" ? 18 : tokenDecimals;
	let amountInWei: bigint | null = null;
	try {
		if (deferredAmount && deferredAmount !== "0") {
			const parsed = parseUnits(deferredAmount, inputDecimals);
			if (parsed > 0n) {
				amountInWei = parsed;
			}
		}
	} catch {
		// Invalid input string — leave null
	}

	const poolReady = poolState.lpFee !== undefined && poolState.sqrtPriceX96 !== undefined;
	const queryEnabled = amountInWei !== null && poolReady;

	const {
		data: quote,
		isLoading: quoteLoading,
		error: quoteError,
		refetch,
	} = useQuery<SwapQuote | null>({
		queryKey: ["swap-quote", tokenAddress, poolAddress, deferredAmount, direction, slippageBps],
		queryFn: async () => {
			// Re-check inside queryFn — TanStack Query can call after enabled flips
			if (amountInWei === null || poolState.lpFee === undefined) {
				return null;
			}

			const activeChain = getActiveChain();
			const addresses = getChainAddresses(activeChain.id);
			if (!addresses) {
				return null;
			}

			const zeroForOne = getZeroForOne(tokenAddress, direction, activeChain.id);
			const poolKey = buildPoolKey(tokenAddress, poolAddress, addresses.weth);

			const { result } = await simulateContract(wagmiConfig, {
				address: addresses.quoter,
				abi: quoterAbi,
				functionName: "quoteExactInputSingle",
				args: [
					{
						poolKey,
						zeroForOne,
						exactAmount: amountInWei,
						hookData: "0x" satisfies `0x${string}`,
					},
				],
			});

			const [amountOut] = result;
			const amountOutBig = BigInt(amountOut);

			const lpFee = poolState.lpFee;
			const protocolFee = poolState.protocolFee ?? 0;

			// Fee breakdown in token amounts (approximate, based on input)
			const lpFeeAmount = (amountInWei * BigInt(lpFee)) / PIPS_DENOMINATOR;
			const protocolFeeAmount = (amountInWei * BigInt(protocolFee)) / PIPS_DENOMINATOR;

			// Effective rate via scaled bigint division
			const effectiveRate =
				amountInWei > 0n
					? Number((amountOutBig * RATE_SCALE) / amountInWei) / Number(RATE_SCALE)
					: 0;

			const priceImpact = poolState.sqrtPriceX96
				? computePriceImpact(poolState.sqrtPriceX96, amountInWei, amountOutBig, zeroForOne)
				: 0;

			const minimumReceived = calculateMinimumOutput(amountOutBig, slippageBps);

			return {
				amountOut: amountOutBig,
				priceImpact,
				feeBreakdown: {
					lpFeePips: lpFee,
					protocolFeePips: protocolFee,
					lpFeeAmount,
					protocolFeeAmount,
				},
				effectiveRate,
				minimumReceived,
				direction,
			};
		},
		enabled: queryEnabled,
		refetchInterval: 12_000,
		staleTime: 10_000,
	});

	return {
		quote: quote ?? null,
		isLoading: poolState.isLoading || (queryEnabled && quoteLoading),
		error: poolState.error ?? (quoteError instanceof Error ? quoteError : null),
		noLiquidity,
		refetch,
	};
}
